import React from "react";

import { toast } from "react-toastify";

import {
  useApiMembersEmployeeConditionSubmissionsDestroy,
  useApiMembersEmployeeConditionSubmissionsBookmarksCreate,
  useApiMembersEmployeeConditionSubmissionsBookmarksDestroy,
  useApiMembersEmployeeConditionSubmissionsLikesCreate,
  useApiMembersEmployeeConditionSubmissionsLikesDestroy,
  useBoolean,
  useClipboard,
  useCounter,
  useFooters,
  useProvidersCurrentEmployee,
} from "~/hooks";

import {
  EmployeeConditionSubmissionsLikedEmployeesModal,
  EmployeeSurveyQuestionDetail,
  PostFooter,
  PostHeader,
} from "~/components/organisms";

import { EmployeeConditionSubmissionType } from "~/domains";

import { Href } from "~/constants/postUrl";

import { Button, PostDetailWrapper } from "~/ui";

type PropsType = {
  employeeConditionSubmission: EmployeeConditionSubmissionType;
  className?: string;
  openReply: (value: string) => void;
  refetch?: () => void;
  canDelete: boolean;
};

export const EmployeeConditionSubmissionDetail = ({
  employeeConditionSubmission,
  openReply,
  refetch,
  canDelete,
  className = "",
}: PropsType) => {
  const { currentEmployee } = useProvidersCurrentEmployee();
  const { clipboardToCopy } = useClipboard();
  const { isChecked: isOpen, setTrue, setFalse } = useBoolean(false);
  const {
    isChecked: liked,
    setTrue: setLiked,
    setFalse: setNotLiked,
  } = useBoolean(employeeConditionSubmission.liked);
  const {
    count: likeCount,
    increment: likeIncrement,
    decrement: likeDecrement,
  } = useCounter(
    employeeConditionSubmission.employeeConditionSubmissionStat.likesCount,
  );
  const {
    isChecked: bookmarked,
    setTrue: setBookmarked,
    setFalse: setNotBookmarked,
  } = useBoolean(employeeConditionSubmission.bookmarked);
  const {
    count: bookmarkCount,
    increment: bookmarkIncrement,
    decrement: bookmarkDecrement,
  } = useCounter(
    employeeConditionSubmission.employeeConditionSubmissionStat.bookmarksCount,
  );
  const {
    isChecked: isLikedEmployeesModalOpen,
    setTrue: setLikedEmployeesModalOpen,
    setFalse: setLikedEmployeesModalClose,
  } = useBoolean(false);

  const { mutate: likeCreateRequest } =
    useApiMembersEmployeeConditionSubmissionsLikesCreate();
  const { mutate: likeDeleteRequest } =
    useApiMembersEmployeeConditionSubmissionsLikesDestroy();

  const { mutate: bookmarkCreateRequest } =
    useApiMembersEmployeeConditionSubmissionsBookmarksCreate();
  const { mutate: bookmarkDeleteRequest } =
    useApiMembersEmployeeConditionSubmissionsBookmarksDestroy();
  const { mutate: destroyRequest } =
    useApiMembersEmployeeConditionSubmissionsDestroy();
  const handleCreateLike = () => {
    likeCreateRequest(
      {
        employeeConditionSubmissionId: employeeConditionSubmission.id,
      },
      {
        onSuccess: () => {
          setLiked();
          likeIncrement();
        },
      },
    );
  };

  const handleDeleteLike = () => {
    likeDeleteRequest(
      {
        employeeConditionSubmissionId: employeeConditionSubmission.id,
      },
      {
        onSuccess: () => {
          setNotLiked();
          likeDecrement();
        },
      },
    );
  };

  const handleCreateBookmark = () => {
    bookmarkCreateRequest(
      {
        employeeConditionSubmissionId: employeeConditionSubmission.id,
      },
      {
        onSuccess: () => {
          setBookmarked();
          bookmarkIncrement();
        },
      },
    );
  };

  const handleDeleteBookmark = () => {
    bookmarkDeleteRequest(
      {
        employeeConditionSubmissionId: employeeConditionSubmission.id,
      },
      {
        onSuccess: () => {
          setNotBookmarked();
          bookmarkDecrement();
        },
      },
    );
  };

  const handleDelete = () => {
    const isConfirm = confirm("本当に削除しますか？");
    if (!isConfirm) return;
    destroyRequest(
      {
        employeeConditionSurveyId:
          employeeConditionSubmission.employeeConditionSurvey.id,
        id: employeeConditionSubmission.id,
      },
      {
        onSuccess: (data) => {
          toast(data.message);
          refetch?.();
        },
      },
    );
  };

  const leftFooters = useFooters({
    like: {
      doneAlready: liked,
      undoOnClick: handleDeleteLike,
      doOnClick: handleCreateLike,
      count: likeCount,
      textOnClick: setLikedEmployeesModalOpen,
    },
    bookmark: {
      doneAlready: bookmarked,
      undoOnClick: handleDeleteBookmark,
      doOnClick: handleCreateBookmark,
      count: bookmarkCount,
    },
    comment: {
      count:
        employeeConditionSubmission.employeeConditionSubmissionStat
          .repliesCount,
      undoOnClick: () => openReply(employeeConditionSubmission.id),
      doOnClick: () => openReply(employeeConditionSubmission.id),
      doneAlready: Boolean(
        employeeConditionSubmission.employeeConditionSubmissionStat
          .repliesCount,
      ),
    },
  });

  const sortedEmployeeConditionSurveyQuestions =
    employeeConditionSubmission.employeeConditionSurvey.questions.sort(
      (a, b) => a.displayOrder - b.displayOrder,
    );
  const questions = isOpen
    ? sortedEmployeeConditionSurveyQuestions
    : sortedEmployeeConditionSurveyQuestions.slice(0, 1);

  return (
    <div
      className={`${className}`}
      id={`employeeConditionSubmission-${employeeConditionSubmission.id}`}
    >
      <PostDetailWrapper>
        <PostHeader
          postedEmployee={employeeConditionSubmission.employee}
          miniText={employeeConditionSubmission.postDate}
          underLabel={employeeConditionSubmission.employeeConditionSurvey.title}
          menuItems={[
            {
              icon: "ioCopyOutline",
              text: "リンクをコピー",
              onClick: () =>
                clipboardToCopy(
                  `${location.origin}${Href["EmployeeCondition::Submission"](
                    employeeConditionSubmission.id,
                  )}`,
                ),
              disabled: false,
            },
            {
              icon: "ioTrashOutline",
              text: "削除",
              onClick: () => handleDelete(),
              disabled:
                !canDelete ||
                currentEmployee?.id !== employeeConditionSubmission.employee.id,
            },
          ]}
        />
        <div className="mt-8 space-y-8 px-4">
          <div className="space-y-8">
            {questions.map((question) => (
              <EmployeeSurveyQuestionDetail
                key={question.id}
                question={question}
                options={question.options || []}
                answers={
                  employeeConditionSubmission.employeeConditionSubmissionAnswers?.map(
                    (a) => ({
                      questionId: a.employeeConditionSurveyQuestionId,
                      optionId: a.employeeConditionSurveyQuestionOptionId || "",
                      answer: a.answer,
                    }),
                  ) || []
                }
              />
            ))}
          </div>
          {isOpen ? (
            <Button
              text="閉じる"
              onClick={setFalse}
              color="gray"
              outline
              className="w-full"
              size="sm"
            />
          ) : (
            <Button
              text="もっと見る"
              onClick={setTrue}
              color="gray"
              outline
              className="w-full"
              size="sm"
            />
          )}
        </div>
        <PostFooter iconWithCounters={leftFooters.footers} className="mt-3" />
        {isLikedEmployeesModalOpen && (
          <EmployeeConditionSubmissionsLikedEmployeesModal
            employeeConditionSubmissionId={employeeConditionSubmission.id}
            onClose={setLikedEmployeesModalClose}
          />
        )}
      </PostDetailWrapper>
    </div>
  );
};
